import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { useGoogleCalendarAuth } from '@/hooks/useGoogleCalendarAuth';
import { useUserIcons } from '@/hooks/useUserIcons';
import { IconPicker } from './IconPicker';
import { Loader2, Calendar, Trash2, RefreshCw, Smile, Bell, Users } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { UserTable } from '@/components/UserManagement/UserTable';

interface UserSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  userColor: string;
}

export const UserSettingsDialog = ({
  open,
  onOpenChange,
  userId,
  userName,
  userColor,
}: UserSettingsDialogProps) => {
  const [googleEmail, setGoogleEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { initiateAuth, disconnectCalendar, checkConnectionStatus } = useGoogleCalendarAuth();
  const { getUserIcon, setUserIcon, isSaving } = useUserIcons();
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<{
    isConnected: boolean;
    googleEmail: string | null;
    connectedAt: string | null;
  }>({ isConnected: false, googleEmail: null, connectedAt: null });
  const [notificationsOverdueEnabled, setNotificationsOverdueEnabled] = useState(true);
  const [notificationsCompletionEnabled, setNotificationsCompletionEnabled] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);

  useEffect(() => {
    if (open) {
      loadConnectionStatus();
      setSelectedIcon(getUserIcon(userId));
      if (userId === 'asaf') {
        loadNotificationSettings();
      }
    }
  }, [open, userId]);

  const loadNotificationSettings = async () => {
    const { data } = await supabase
      .from('user_settings')
      .select('notifications_overdue_enabled, notifications_completion_enabled')
      .eq('user_name', 'asaf')
      .single();
    
    if (data) {
      setNotificationsOverdueEnabled(data.notifications_overdue_enabled ?? true);
      setNotificationsCompletionEnabled(data.notifications_completion_enabled ?? true);
    }
  };

  const handleNotificationToggle = async (type: 'overdue' | 'completion', enabled: boolean) => {
    setSavingNotifications(true);
    const updateData = type === 'overdue' 
      ? { notifications_overdue_enabled: enabled }
      : { notifications_completion_enabled: enabled };
    
    const { error } = await supabase
      .from('user_settings')
      .upsert({ 
        user_name: 'asaf',
        ...updateData 
      }, { onConflict: 'user_name' });
    
    if (error) {
      toast.error('שגיאה בשמירת ההגדרות');
    } else {
      if (type === 'overdue') {
        setNotificationsOverdueEnabled(enabled);
      } else {
        setNotificationsCompletionEnabled(enabled);
      }
      toast.success('ההגדרות נשמרו');
    }
    setSavingNotifications(false);
  };

  const loadConnectionStatus = async () => {
    setIsLoading(true);
    const status = await checkConnectionStatus(userId);
    setConnectionStatus(status);
    setIsLoading(false);
  };

  const handleConnect = () => {
    if (!googleEmail.trim()) {
      return;
    }
    initiateAuth(userId, googleEmail.trim());
    onOpenChange(false);
  };

  const handleDisconnect = async () => {
    setIsLoading(true);
    await disconnectCalendar(userId);
    await loadConnectionStatus();
    setIsLoading(false);
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    await loadConnectionStatus();
    setIsLoading(false);
  };

  const handleIconSelect = async (iconName: string) => {
    setSelectedIcon(iconName);
    await setUserIcon(userId, iconName);
  };

  const getTabsCount = () => {
    if (userId === 'asaf') return 4;
    return 2;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className={`${userId === 'asaf' ? 'sm:max-w-4xl' : 'sm:max-w-md'} bg-slate-800 border-slate-700 text-slate-100`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            הגדרות - {userName}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="icon" className="w-full">
          <TabsList className={`grid w-full grid-cols-${getTabsCount()} bg-slate-700`}>
            <TabsTrigger value="icon" className="gap-2 data-[state=active]:bg-slate-600 data-[state=active]:text-slate-100 text-slate-300">
              <Smile className="h-4 w-4" />
              אייקון
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2 data-[state=active]:bg-slate-600 data-[state=active]:text-slate-100 text-slate-300">
              <Calendar className="h-4 w-4" />
              יומן
            </TabsTrigger>
            {userId === 'asaf' && (
              <>
                <TabsTrigger value="notifications" className="gap-2 data-[state=active]:bg-slate-600 data-[state=active]:text-slate-100 text-slate-300">
                  <Bell className="h-4 w-4" />
                  התראות
                </TabsTrigger>
                <TabsTrigger value="users" className="gap-2 data-[state=active]:bg-slate-600 data-[state=active]:text-slate-100 text-slate-300">
                  <Users className="h-4 w-4" />
                  משתמשים
                </TabsTrigger>
              </>
            )}
          </TabsList>
          
          <TabsContent value="icon" className="mt-4">
            <div className="space-y-3">
              <Label className="text-slate-300">בחר אייקון שייצג אותך</Label>
              <IconPicker
                selectedIcon={selectedIcon}
                onSelectIcon={handleIconSelect}
                userColor={userColor}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="calendar" className="mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
              </div>
            ) : connectionStatus.isConnected ? (
              <div className="space-y-4">
                <div className="rounded-lg bg-green-900/30 border border-green-700 p-4 space-y-2">
                  <p className="text-sm font-medium text-green-300">
                    ✅ מחובר ליומן Google
                  </p>
                  <p className="text-xs text-green-400">
                    {connectionStatus.googleEmail}
                  </p>
                  {connectionStatus.connectedAt && (
                    <p className="text-xs text-green-500">
                      מחובר מאז:{' '}
                      {format(new Date(connectionStatus.connectedAt), 'dd/MM/yyyy HH:mm', {
                        locale: he,
                      })}
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleRefresh}
                    className="flex-1 bg-slate-700 border-slate-600 text-slate-100 hover:bg-slate-600"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    רענן סנכרון
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDisconnect}
                    className="flex-1"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    נתק
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="google-email" className="text-slate-300">כתובת Gmail</Label>
                  <Input
                    id="google-email"
                    type="email"
                    placeholder="example@gmail.com"
                    value={googleEmail}
                    onChange={(e) => setGoogleEmail(e.target.value)}
                    dir="ltr"
                    className="bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400"
                  />
                  <p className="text-xs text-slate-400">
                    הזן את כתובת ה-Gmail שלך כדי לחבר את היומן
                  </p>
                </div>

                <Button
                  onClick={handleConnect}
                  disabled={!googleEmail.trim()}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  חבר יומן Google
                </Button>
              </div>
            )}
          </TabsContent>

          {userId === 'asaf' && (
            <>
              <TabsContent value="notifications" className="mt-4">
                <div className="space-y-4">
                  <p className="text-sm text-slate-400">
                    שלוט בהתראות שנשלחות ל-Home Assistant
                  </p>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700">
                    <div className="space-y-1">
                      <Label className="text-slate-200">התראות משימות שהושלמו</Label>
                      <p className="text-xs text-slate-400">שלח התראה כאשר משימה הושלמה</p>
                    </div>
                    <Switch
                      checked={notificationsCompletionEnabled}
                      onCheckedChange={(checked) => handleNotificationToggle('completion', checked)}
                      disabled={savingNotifications}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700">
                    <div className="space-y-1">
                      <Label className="text-slate-200">התראות משימות באיחור</Label>
                      <p className="text-xs text-slate-400">שלח התראה כאשר משימה לא הושלמה בזמן</p>
                    </div>
                    <Switch
                      checked={notificationsOverdueEnabled}
                      onCheckedChange={(checked) => handleNotificationToggle('overdue', checked)}
                      disabled={savingNotifications}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="users" className="mt-4">
                <UserTable />
              </TabsContent>
            </>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};